import {
  Paper,
  Stack,
  Box,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import { useLangContext } from "../../../context/langContext";
import { Link } from "react-router-dom";
import { FileSchema } from "../../../services/Schemas/File";
import { formatDateWithTime } from "../../../utils/date-formatter";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import React from "react";
import UserLink from "../common/UserLink";
import { useUrl } from "../../../hooks/urlEncoding";
import { routerPaths } from "../../../constants/Paths";
import { UsersListColumnFilterable } from "../../../services/Endpoints/users/UsersListEndpoint";
import { FilterOperatorEnum } from "../../../services/Schemas/ListQueryString";

const _support = { fa: "پشتیبان", en: "support" };

interface IMessageCartProps {
  name: string;
  messageText?: string;
  messageImage?: FileSchema | null;
  createdAt?: string;
  isSupportUser?: boolean;
  mobile?: string;
  isPrivate: boolean;
}

function parseMarkdown(text: string) {
  const codeBlockRegex = /```([\s\S]*?)```/g;

  const parts: React.ReactNode[] = [];
  let lastIndex = 0;

  let match;
  while ((match = codeBlockRegex.exec(text)) !== null) {
    const index = match.index;
    const beforeCodeRaw = text.slice(lastIndex, index);

    parts.push(
      <Box key={`before-${index}`} sx={{ mt: 0.5 }}>
        {splitWithLinks(beforeCodeRaw)}
      </Box>
    );

    const code = match[1];

    parts.push(
      <Box
        key={`code-${index}`}
        sx={{
          bgcolor: "gray.100",
          fontFamily: "monospace",
          p: 1,
          borderRadius: "6px",
          position: "relative",
          whiteSpace: "pre-wrap",
          my: 1,
          direction: "ltr",
        }}
      >
        <Tooltip title="Copy">
          <IconButton
            size="small"
            sx={{ position: "absolute", top: 4, right: 4 }}
            onClick={() => navigator.clipboard.writeText(code)}
          >
            <ContentCopyIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Typography variant="caption" component="code">
          {code}
        </Typography>
      </Box>
    );

    lastIndex = index + match[0].length;
  }

  const restRaw = text.slice(lastIndex);
  parts.push(
    <Box key={`after-${lastIndex}`} sx={{ mt: 0.5 }}>
      {splitWithLinks(restRaw)}
    </Box>
  );

  return parts;
}

function splitWithLinks(text: string): React.ReactNode[] {
  const linkRegex = /(https?:\/\/[^\s]+)/g;
  const segments = text.split(linkRegex);

  return segments.map((segment, index) => {
    if (linkRegex.test(segment)) {
      try {
        const url = new URL(segment);

        if (url.protocol === "http:" || url.protocol === "https:") {
          return (
            <a
              key={`link-${index}`}
              href={segment}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                direction: "ltr",
                display: "inline-block",
                color: "#1976d2",
                wordBreak: "break-word",
              }}
            >
              {segment}
            </a>
          );
        }
      } catch {}
    }

    return <React.Fragment key={`text-${index}`}>{segment}</React.Fragment>;
  });
}

export default function MessageCard({
  messageText,
  name,
  createdAt,
  isSupportUser,
  messageImage,
  mobile,
  isPrivate,
}: Readonly<IMessageCartProps>) {
  const { dir, lang } = useLangContext();
  const _profileImage = !isSupportUser
    ? "/images/userIcon.svg"
    : "/images/supportIcon.svg";

  const { queryStringUrl } = useUrl();

  return (
    <Stack
      justifyContent="start"
      alignItems="end"
      direction={isSupportUser ? "row" : "row-reverse"}
      ml={!isSupportUser ? "auto" : 0}
      mr={isSupportUser ? "auto" : 0}
    >
      <Paper
        sx={{
          background: (theme) =>
            isSupportUser ? theme.palette.gray[200] : theme.palette.gray.bg,
          boxShadow: "none",
          py: "16px",
          px: "10px",
          borderRadius: "12px",
          minWidth: { xs: "240px", md: "300px" },
          maxWidth: { xs: "280px", md: "400px" },
          borderBottomLeftRadius: isSupportUser ? "2px" : "12px",
          borderBottomRightRadius: isSupportUser ? "12px" : "2px",
        }}
      >
        <Typography
          dir={dir}
          sx={{
            color: (theme) => theme.palette.dominant[400],
            display: "flex",
            justifyContent: "space-between",
            width: "calc(100% - 10px)",
            whiteSpace: "nowrap",
            mb: "10px",
          }}
        >
          <Stack
            direction="row"
            gap="10px"
            alignItems="center"
            justifyContent="start"
          >
            {isSupportUser && (
              <Typography
                variant="labelRegular"
                sx={{
                  bgcolor: (theme) => theme.palette.dominant.main,
                  color: (theme) => theme.palette.gray.bg,
                  py: "2px",
                  px: "8px",
                  borderRadius: "6px",
                }}
              >
                {_support[lang]}
              </Typography>
            )}
            {isPrivate && (
              <Typography
                variant="labelRegular"
                sx={{
                  bgcolor: (theme) => theme.palette.others.doneBg,
                  color: (theme) => theme.palette.others.done,
                  py: "2px",
                  px: "8px",
                  borderRadius: "6px",
                }}
              >
                چت خصوصی
              </Typography>
            )}
          </Stack>
          {mobile && (
            <UserLink
              user={{ name, mobile, id: 1 }}
              link={queryStringUrl(routerPaths.USERS, {
                filters: {
                  [UsersListColumnFilterable.MOBILE]: {
                    column: UsersListColumnFilterable.MOBILE,
                    operator: FilterOperatorEnum.CONTAINS,
                    value: mobile,
                  },
                },
              })}
            />
          )}
        </Typography>

        <Typography
          variant="captionRegular"
          color={(theme) => theme.palette.dark[30]}
          dir={dir}
          sx={{
            my: "10px",
            mx: "10px",
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            overflowWrap: "break-word",
            display: "contents",
          }}
        >
          {messageText && parseMarkdown(messageText)}

          {messageImage && (
            <Stack
              direction={{ xs: "column-reverse", md: "row" }}
              justifyContent={isSupportUser ? "start" : "end"}
              alignItems="center"
              gap="10px"
            >
              <Stack gap="6px">
                <Typography
                  variant="captionRegular"
                  color={(theme) => theme.palette.dark[30]}
                  sx={{ ml: "10px", display: "flex", gap: "10px" }}
                >
                  {messageImage.name}
                </Typography>

                <Typography
                  variant="labelRegular"
                  color={(theme) => theme.palette.dark[30]}
                  sx={{
                    ml: "10px",
                    display: "flex",
                    gap: "10px",
                    flexDirection: "row-reverse",
                    justifyContent: "end",
                  }}
                >
                  {Number(messageImage.size) / 1000} KB
                  <Typography
                    variant="labelRegular"
                    component={Link}
                    target="_blank"
                    color={(theme) => theme.palette.others.link}
                    sx={{
                      textDecoration: "none",
                    }}
                    to={messageImage.url}
                  >
                    Download
                  </Typography>
                </Typography>
              </Stack>
              <Stack
                sx={{
                  borderRadius: "12px",
                  overflow: "hidden",
                  width: "113px",
                  height: "73px",
                  objectFit: "cover",
                }}
              >
                <img
                  src={messageImage.url}
                  width="113px"
                  height="73px"
                  alt="message_image"
                />
              </Stack>
            </Stack>
          )}
        </Typography>
        <Typography
          component="p"
          textAlign={isSupportUser ? "start" : "end"}
          width="100%"
          variant="labelRegular"
          marginTop={"5px"}
          color={(theme) => theme.palette.dominant[400]}
        >
          {formatDateWithTime({ lang, date: createdAt! })}
        </Typography>
      </Paper>
      <Stack
        sx={{
          scale: "0.9",
        }}
      >
        <img src={_profileImage} alt="user_image" />
      </Stack>
    </Stack>
  );
}
